import { BoxProps } from '@chakra-ui/react';
import { noop } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { LocationPicker, useLocationPicker, useSnackbar } from 'ui-components';
import { SNACKBAR_ERROR_GENERIC_OPTIONS } from '~/constants';
import { useGetLocationDetails } from '~/hooks/api/useGetLocationDetails';
import { useGetLocationSuggestions } from '~/hooks/api/useGetLocationSuggestions';

export type CreatorLocationDetail = {
  city: string;
  country: string;
  countryCode: string;
  countryCodeAlpha3: string;
  districtName: string;
  fullAddress: string;
  geo: {
    lat: number;
    lon: number;
  };
  id: string;
  mapboxId: string;
  placeFormatted: string;
  postCode: string;
  regionCode: string;
  regionCodeFull: string;
  regionName: string;
  type: string;
} | null;

export interface LocationPickerSuggestion {
  name: string;
  name_preferred?: string;
  mapbox_id: string;
  feature_type: string;
  place_formatted: string;
  full_address?: string;
  context: {
    country: {
      id: string;
      name: string;
      country_code: string;
      country_code_alpha_3: string;
    };
    region?: {
      id: string;
      name: string;
      region_code: string;
      region_code_full: string;
    };
    district?: {
      id: string;
      name: string;
    };
  };
  language: string;
  maki: string;
  metadata: Record<string, unknown>;
}

export type CreatorLocationSuggestion = LocationPickerSuggestion | null;

interface SearchLocationProps extends Omit<BoxProps, 'onError'> {
  creatorLocation: CreatorLocationDetail | null;
  setCreatorLocation: (creatorLocation: CreatorLocationDetail | null) => void;
  onError?: (error?: boolean) => void;
  trackId?: string;
  isSmartData?: boolean;
}

export const getLocationFriendlyName = (location: CreatorLocationDetail) => {
  if (!location && typeof location !== 'object') return null;

  const { city, regionName, country } = location || {};
  return [city, regionName, country].filter(Boolean).join(', ');
};

export function SearchLocation({
  creatorLocation,
  setCreatorLocation,
  trackId,
  isSmartData,
  onError = noop,
  ...props
}: SearchLocationProps) {
  const snackbar = useSnackbar();

  const [locationSearch, setLocationSearch] =
    useState<CreatorLocationSuggestion | null>(null);
  const { data: locationDetails } = useGetLocationDetails(
    locationSearch?.mapbox_id,
  );

  const { getSuggestions } = useGetLocationSuggestions({
    onError: () => snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS),
  });

  const handleChange = useCallback(
    (updatedValue: CreatorLocationSuggestion) => {
      setLocationSearch(updatedValue);
      !updatedValue && setCreatorLocation(updatedValue);
    },
    [setLocationSearch, setCreatorLocation],
  );

  const friendlyName = useMemo(
    () => getLocationFriendlyName(creatorLocation),
    [creatorLocation],
  );

  const context = useLocationPicker({
    value: friendlyName,
    onChange: handleChange,
    isSmartData,
    mapBox: {
      // @ts-ignore
      getLocationSuggestions: getSuggestions,
    },
    inputProps: {
      name: 'location',
      trackId,
      isSmartData,
    },
  });
  const { isEmpty, hasAborted, isOpen } = context;

  useEffect(() => {
    !!locationDetails && setCreatorLocation(locationDetails);
  }, [locationDetails, setCreatorLocation]);

  useEffect(() => {
    onError(isEmpty || hasAborted || isOpen);
  }, [isEmpty, hasAborted, isOpen, onError]);

  return <LocationPicker ctx={context} {...props} />;
}
